<template>
  <div id="app" class="app" :class="$route.name.toLowerCase()">
    <app-header :style="loadingStyle" v-if="isLoaderShow"></app-header>
    <div ref="main" :style="loadingStyle" class="main-wrapper" id="mainWrapper">
      <main class="main">
        <transition
          v-show="isLoaded"
          mode="out-in"
          @enter="enter"
          @leave="leave"
          :css="false"
        >
          <router-view
            :mouse="mI"
            :mouseNoI="m"
            :scrollContent="scrollContent"
            :initObserver="initObserver"
            :is-desktop="isDesktop"
            ref="page"
          ></router-view>
        </transition>
      </main>
    </div>
    <shapes
      :style="loadingStyle"
      ref="$shapes"
      :mouse="mI"
      :number="numberOfShapes"
      :is-dekstop="isDesktop"
      :is-visible="isShapesVisible"
    ></shapes>
    <loader
      ref="loader"
      @shown="onLoaderShown"
      @hidden="onLoaderHidden"
      :isVisible="isLoading"
      :percentage="loaderProgress"
    ></loader>
  </div>
</template>

<script>
import "intersection-observer";
import createjs from "preload-js";
import { TweenMax } from "gsap";
import AppHeader from "@/components/AppHeader";
import Shapes from "@/components/Shapes";
import Loader from "@/components/Loader";
import * as UMath from "@/util/UMath";
import EventBus from "@/util/EventBus.js";

export default {
  name: "app",
  components: {
    AppHeader,
    Shapes,
    Loader,
  },

  data() {
    return {
      SCROLL_INERTIA: 0.07,
      MOUSE_INERTIA: 0.05,
      TILT_INERTIA: 0.04,
      observerRatio: 0.5,

      isLoading: null,
      loaderProgress: 0,
      isLoaded: false,
      isLoaderShow: false,

      $body: null,
      $mainWrapper: null,
      $window: null,

      appear: true,

      // mouse position
      m: {
        x: null,
        y: null,
      },

      // mouse position with inertia
      mI: {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      },

      loadingStyle: {
        // here i use visibility to avoid overwrite opacity used for animation of different component
        visibility: "hidden",
      },

      sY: null, // scroll Y
      siY: null, // scroll inertia Y

      previousRoute: null,
      observerInitialized: false,

      isDesktop: window.innerWidth > 1024,
      isShapesVisible: null,
    };
  },

  computed: {
    numberOfShapes() {
      return this.isDesktop ? 23 : 8;
    },
  },

  created() {
    this.queue = new createjs.LoadQueue(true, null, true);
    this.queue.on("complete", this.onImageLoaded, this);
    this.$store.dispatch("FETCH_EMAIL");
    this.$store.dispatch("FETCH_ABOUT_CONTENT");
  },

  mounted() {
    const { name } = this.$route;
    this.previousRoute = name;

    this.$body = document.querySelector("body");
    this.$mainWrapper = document.querySelector("#mainWrapper");
    this.$window = window;

    this.m = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };

    this.bindEvents();
    this.onResize();

    TweenMax.to(this, 0.8, {
      loaderProgress: 80,
      delay: 0.2,
      ease: Expo.easeOut,
    });
    this.isLoading = true;
    this.$store.dispatch("FETCH_PROJECTS").then(() => {
      this.queue.loadManifest(this.getAssetsToLoad());
    });

    // fake appear animation
    TweenMax.set(this.$refs.main, { autoAlpha: 0 });

    this.$refs.loader.$once("before-hide", () => {
      TweenMax.fromTo(
        this.$refs.main,
        0.8,
        {
          autoAlpha: 0,
          y: -40,
        },
        {
          y: 0,
          autoAlpha: 1,
          delay: 0.3,
          ease: Quart.easeOut,
          force3D: true,
          onComplete: () => {
            this.setBodyHeight();
          },
        }
      );
    });
  },

  methods: {
    enter(el, done) {
      this.resetScroll();

      this.$refs.page.enter
        ? this.$refs.page.enter(done)
        : TweenMax.from(el, 0.5, {
            autoAlpha: 0,
            ease: Quad.easeOut,
            force3D: true,
            onComplete: done,
          });

      this.setBodyHeight();
      this.verifyShapesVisibility();
    },

    leave(el, done) {
      this.$refs.page.leave
        ? this.$refs.page.leave(done)
        : TweenMax.to(el, 0.35, {
            autoAlpha: 0,
            ease: Quad.easeOut,
            force3D: true,
            onComplete: done,
          });
    },

    bindEvents() {
      TweenLite.ticker.addEventListener("tick", this.rAF.bind(this));

      window.addEventListener("resize", this.onResize.bind(this));

      if (this.isDesktop) {
        window.addEventListener("mousemove", this.updateMousePos.bind(this));
      }
    },

    updateMousePos(e) {
      this.m = {
        x: e.clientX,
        y: e.clientY,
      };
    },

    rAF() {
      this.setRafProps();
      EventBus.$emit("rAF");
    },

    setRafProps() {
      this.sY = this.$window.scrollY || this.$window.pageYOffset;
      this.siY = UMath.getInertia(this.sY, this.siY, this.SCROLL_INERTIA);

      if (this.isDesktop) {
        this.mI = {
          x: UMath.getInertia(this.m.x, this.mI.x, this.MOUSE_INERTIA),
          y: UMath.getInertia(this.m.y, this.mI.y, this.MOUSE_INERTIA),
        };
      }
    },

    onResize() {
      this.setBodyHeight();
      this.resetScroll();

      this.isDesktop = window.innerWidth > 1024;
    },

    initObserver() {
      var $sections = document.querySelectorAll(".js-observer");

      var options = {
        threshold: this.observerRatio,
      };

      this.observer = new IntersectionObserver(
        this.onIntersection.bind(this),
        options
      );

      var $section;

      for (var i = 0; i < $sections.length; i++) {
        $section = $sections[i];
        this.observer.observe($section);
      }
    },

    onIntersection(entries) {
      var entry;
      var $target;

      for (var e = 0; e < entries.length; e++) {
        entry = entries[e];

        if (entry.intersectionRatio > this.observerRatio) {
          $target = entry.target;
          $target.classList.add("is-visible");
          this.observer.unobserve(entry.target);
        }
      }
    },

    setBodyHeight() {
      if (this.isDesktop) {
        TweenMax.set(this.$body, { height: this.$mainWrapper.offsetHeight });
      }
    },

    scrollContent() {
      if (this.isDesktop) {
        TweenMax.set(this.$mainWrapper, { y: -this.siY, force3D: true });
      }
    },

    resetScroll() {
      this.sY = this.siY = 0;
      TweenMax.set(this.$mainWrapper, { y: 0, force3D: true });
    },

    verifyShapesVisibility() {
      if (["NotFound", "Project", "Home"].includes(this.$route.name)) {
        this.isShapesVisible = false;
        return;
      }

      if (this.isDesktop) {
        this.isShapesVisible = true;
      } else {
        this.isShapesVisible = this.$route.name === "Home";
      }
    },

    getAssetsToLoad() {
      let manifest = [];

      Object.entries(this.$store.state.projects).forEach(([key, project]) => {
        manifest.push({
          src: project.better_featured_image.media_details.source_url,
          id: `project-${key}-thumbnail`,
        });
      });

      return manifest;
    },

    onImageLoaded() {
      TweenMax.to(this, 0.4, {
        loaderProgress: 100,
        ease: Quart.easeOut,
      });

      TweenMax.delayedCall(0.4, () => {
        this.isLoading = false;

        // set visible if page is not prerendered
        this.loadingStyle = {
          visibility: "visible",
        };
      });
    },

    onLoaderShown() {
      this.isLoaderShow = true;
    },

    onLoaderHidden() {
      this.isLoaded = true;
      this.verifyShapesVisibility();
    },
  },
};
</script>

<style src="./styles/global.scss" lang="scss"></style>
<style lang="scss" rel="stylesheet/scss" scoped>
@import "~include-media/dist/_include-media.scss";

.app {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  @include media("<=desktop") {
    position: static;
  }

  &.notfound {
    height: 100vh;
    overflow: hidden;
  }

  &.is-loading {
    .main-wrapper,
    .header {
      opacity: 0;
    }
  }
}

.main-wrapper {
  padding-top: 125px;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;

  @include media(">=1440px") {
    padding-top: 156px;
  }

  @include media("<=tablet") {
    padding-top: 84px;
  }
}

.main {
  max-width: 78em;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1em;
  padding-right: 1em;
}
</style>



// WEBPACK FOOTER //
// App.vue?f49823fa